// @ai-generated 2026-07-20T11:01:17+00:00 by philipp.kammerer@synqony.com using claude-fable-5

import { EnumMessageLevel, EnumMessageType, type Result } from '@synqo/frontend-component-library';
import { useCallback } from 'react';

/** Default works for Azure block blobs; pass different headers for other providers. */
const defaultHeaders: Record<string, string> = { 'x-ms-blob-type': 'BlockBlob' };

export interface AzureBlobUpload {
    /** Presigned URL issued by the backend for the target object. */
    uploadUrl: string;
    /** Raw file bytes to PUT. */
    file: File;
    /** Request headers for the PUT; replaces the default (Azure block blob header) entirely when set. */
    headers?: Record<string, string>;
    /** Upload progress in percent (0–100). */
    onProgress?: (percent: number) => void;
}

export interface AzureBlobUploadResult {
    /** HTTP status; undefined when the request never produced a response (e.g. network error). */
    status?: number;
}

/**
 * PUTs a file to a presigned storage URL, reporting upload progress.
 *
 * Uses <c>XMLHttpRequest</c> because <c>fetch</c> has no upload progress events
 * (its streaming-body workaround doesn't work in Firefox/Safari).
 *
 * All failure modes resolve to a failed <see cref="Result"/> — branch on <c>isOk</c>, no try/catch needed.
 */
export function useUploadToAzureBlobStorage() {
    return useCallback(
        ({
            uploadUrl,
            file,
            headers = defaultHeaders,
            onProgress,
        }: AzureBlobUpload): Promise<Result<AzureBlobUploadResult>> =>
            new Promise((resolve) => {
                const failure = (message: string): Result<AzureBlobUploadResult> => ({
                    data: undefined,
                    messages: [
                        {
                            type: EnumMessageType.Info,
                            level: EnumMessageLevel.Danger,
                            title: 'Upload failed',
                            message,
                        },
                    ],
                });

                const xhr = new XMLHttpRequest();
                xhr.open('PUT', uploadUrl);
                xhr.setRequestHeader('Content-Type', file.type);
                for (const [name, value] of Object.entries(headers)) {
                    xhr.setRequestHeader(name, value);
                }

                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable && onProgress) {
                        onProgress(Math.round((event.loaded / event.total) * 100));
                    }
                };

                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve({ data: { status: xhr.status }, messages: [] });
                    } else {
                        resolve(failure(`Datei-Upload fehlgeschlagen (Status ${xhr.status}).`));
                    }
                };

                xhr.onerror = () => {
                    resolve(failure('Datei-Upload fehlgeschlagen.'));
                };

                xhr.send(file);
            }),
        [],
    );
}
